import * as Api from '@ViewModels';
import { StyleDeclarationValue, css } from 'aphrodite';
import { observer } from 'mobx-react';
import moment from 'moment';
import * as React from 'react';
import { getDefaultDateStringValue, getDefaultTimeStringValue } from '../../../../../models/UiUtils';
import { baseStyleSheet } from '../../../../styles/styles';
import { SendSpeedDisplay } from '../SendSpeedDisplay';
import { GroupEmailTableRowStatusDisplay } from './GroupEmailTableRowStatusDisplay';
import { styleSheet } from './styles';

interface IProps {
	campaign: Api.ICampaign;
	className?: string;
	impersonationContext?: Api.IImpersonationContext;
	styles?: StyleDeclarationValue[];
}

export const GroupEmailTableRowStatus: React.FC<IProps> = observer(props => {
	const { campaign, ...otherProps } = props;
	const cancelDate = campaign.cancelledDate ? new Date(campaign.cancelledDate) : null;
	const completedDate = campaign.completedDate ? new Date(campaign.completedDate) : null;
	const isForApproval = campaign.approvalRequests && campaign.approvalRequests.length > 0;
	if (cancelDate || campaign.lastDetailedStatus?.statusCode === Api.EmailDetailedStatusCode.Expired_Complete) {
		return (
			<GroupEmailTableRowStatusDisplay
				{...otherProps}
				campaign={campaign}
				color={styleSheet.red}
				date={cancelDate}
				datePrefix={
					campaign.lastDetailedStatus?.statusCode === Api.EmailDetailedStatusCode.Expired_Complete
						? 'automatically on'
						: 'on'
				}
				detailedStatus={campaign.lastDetailedStatus}
				status='Cancelled'
				userRef={campaign.creator}
			/>
		);
	}

	if (completedDate) {
		return (
			<GroupEmailTableRowStatusDisplay
				{...otherProps}
				campaign={campaign}
				color={styleSheet.green}
				date={new Date(campaign.schedule?.startDate)}
				status='Sent'
				userRef={campaign.creator}
			/>
		);
	}

	if (isForApproval) {
		const approval = campaign.approvalRequests[0] ?? {};

		if (approval.response?.status === Api.CampaignApprovalStatus.Rejected) {
			return (
				<GroupEmailTableRowStatusDisplay
					{...otherProps}
					color={styleSheet.red}
					date={new Date(approval.response?.creationDate)}
					status='Rejected'
					userRef={approval.approver}
				/>
			);
		} else if (approval.response?.status === Api.CampaignApprovalStatus.Approved) {
			return (
				<GroupEmailTableRowStatusDisplay
					{...otherProps}
					color={styleSheet.blue}
					date={new Date(campaign.schedule?.startDate)}
					datePrefix='for'
					status='Scheduled'
					userRef={approval.approver}
				/>
			);
		} else {
			// pending
			return (
				<GroupEmailTableRowStatusDisplay
					{...otherProps}
					color={styleSheet.orange}
					date={new Date(campaign.schedule?.startDate)}
					status='Pending approval'
					userRef={campaign.creator}
				/>
			);
		}
	}

	if (campaign.status === Api.EmailSendStatus.Sending) {
		return (
			<GroupEmailTableRowStatusDisplay
				{...otherProps}
				campaign={campaign}
				color={styleSheet.blue}
				date={new Date(campaign.schedule?.startDate)}
				detailedStatus={campaign.lastDetailedStatus}
				status='Sending'
				userRef={campaign.creator}
			/>
		);
	}

	return (
		<GroupEmailTableRowStatusDisplay
			campaign={campaign}
			color={styleSheet.blue}
			date={new Date(campaign.schedule?.startDate)}
			datePrefix='for'
			detailedStatus={campaign.lastDetailedStatus}
			status='Scheduled'
			userRef={campaign.creator}
			{...otherProps}
		/>
	);
});

interface IExpandableDisplayProps {
	actor: string;
	campaign?: Api.ICampaign;
	className?: string;
	color: StyleDeclarationValue;
	date: Date;
	datePrefix?: string;
	sentBy: string;
	status: string;
	styles?: StyleDeclarationValue[];
}

export const GroupEmailTableRowExpandableStatusDisplay: React.FC<IExpandableDisplayProps> = ({
	styles,
	className,
	date,
	actor,
	campaign,
	status,
	sentBy,
	color,
	datePrefix = 'on',
}) => {
	return (
		<div className={`${css(styleSheet.container, ...(styles || []))} ${className || ''}`}>
			<div>
				{actor && <div>Initiated by {actor} </div>}
				<div>
					<span className={css(styleSheet.status, color)}>{status}</span>
					{moment(date).isValid() && (
						<>
							{` ${datePrefix} `}
							<span className={css(baseStyleSheet.fontBold, styleSheet.date)}>
								{getDefaultDateStringValue(moment(date))}
							</span>
							{' at '}
							{getDefaultTimeStringValue(moment(date))}
						</>
					)}
				</div>
				<div>by {sentBy}</div>
				<SendSpeedDisplay campaign={campaign} />
			</div>
		</div>
	);
};
